import {
  BottomSheet,
  Illustration,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Skeleton, Stack } from "@mui/material";
import { useFacilityNotes } from "@src/appV2/Facilities/api/useFacilityNotes";
import { useFacilityProfile } from "@src/appV2/Facilities/api/useFacilityProfile";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { usePostPolicyAcknowledgement } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/usePostPolicyAcknowledgement";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";

import { LoadingButton } from "../../components/LoadingButton";

const DENTAL_FACILITY_TYPES = new Set(["Dental", "Dental Clinic"]);

interface BreakConfirmationBottomSheetProps {
  action?: NoteAcknowledgementAction;
  isLoading?: boolean;
  shiftId: string;
  workplaceId?: string;
  breakPolicyNoteId: string;
  modalState: UseModalState;
  onAcknowledge: () => Promise<void>;
}

function getCTAButtonText(action: NoteAcknowledgementAction) {
  if (action === NoteAcknowledgementAction.BOOK_SHIFT) {
    return "Continue with booking";
  }

  if (action === NoteAcknowledgementAction.CLOCK_IN) {
    return "Continue with clock in";
  }

  if (action === NoteAcknowledgementAction.START_RATE_NEGOTIATION) {
    return "Continue with negotiation";
  }

  return "Continue";
}

export function BreakConfirmationBottomSheet(props: BreakConfirmationBottomSheetProps) {
  const {
    action = NoteAcknowledgementAction.BOOK_SHIFT,
    isLoading = false,
    shiftId,
    workplaceId,
    breakPolicyNoteId,
    modalState,
    onAcknowledge,
  } = props;

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId,
  });

  const { mutateAsync: postBreakPolicyAcknowledgement, isLoading: isAcknowledgingBreakPolicy } =
    usePostPolicyAcknowledgement();

  const { data: facilityNotesData, isInitialLoading: isFacilityNotesLoading } = useFacilityNotes(
    workplaceId ?? "",
    { enabled: isDefined(workplaceId) && isDefined(breakPolicyNoteId) }
  );

  const { data: facilityProfile, isInitialLoading: isFacilityProfileLoading } = useFacilityProfile(
    {
      workplaceId: workplaceId ?? "",
      projection: "type",
    },
    { enabled: isDefined(workplaceId) }
  );

  const breakPolicyNote = facilityNotesData?.find((note) => note.noteId === breakPolicyNoteId);

  useLogEffect(APP_V2_USER_EVENTS.MANDATORY_BREAK_POLICY_VIEWED, {
    action,
    shiftId,
    facilityId: workplaceId,
  });

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onClose={() => {
            logEvent(APP_V2_USER_EVENTS.MANDATORY_BREAK_POLICY_CANCELLED, {
              action,
              shiftId,
              facilityId: workplaceId,
            });
            modalState.closeModal();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            isLoading={
              isLoading ||
              isAcknowledgingBreakPolicy ||
              isFacilityNotesLoading ||
              isFacilityProfileLoading
            }
            size="large"
            variant="contained"
            onClick={async () => {
              logEvent(APP_V2_USER_EVENTS.CLOCK_IN_BREAK_POLICY_ACKNOWLEDGED, {
                shiftId,
                workplaceId,
              });

              await postBreakPolicyAcknowledgement({
                policyAcknowledgementAction: action,
                noteId: breakPolicyNoteId,
              });
              await onAcknowledge();
            }}
          >
            {getCTAButtonText(action)}
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack direction="column" alignItems="center" spacing={8}>
          <Illustration type="break" />
          <Title variant="h3" component="h3" sx={{ textAlign: "center" }}>
            {mandatoryBreakDurationInMinutes}-minute break required
          </Title>
          <Stack direction="column" spacing={4}>
            <Text color={(theme) => theme.palette.text.secondary} variant="body1">
              This facility has the following break policy:
            </Text>
            {isFacilityNotesLoading && (
              <Stack direction="column" spacing={1}>
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
                <Skeleton variant="text" height={24} />
              </Stack>
            )}
            {!isFacilityNotesLoading && (
              <Text
                color={(theme) => theme.palette.text.secondary}
                variant="body1"
                sx={{ fontStyle: "italic", lineHeight: 1.25 }}
              >
                {breakPolicyNote?.note?.trim()}
              </Text>
            )}
            <Text
              color={(theme) => theme.palette.text.secondary}
              variant="body1"
              sx={{ lineHeight: 1.25 }}
            >
              {facilityProfile?.type && DENTAL_FACILITY_TYPES.has(facilityProfile.type) ? (
                <>
                  This facility requires a mandatory {mandatoryBreakDurationInMinutes}-minute unpaid
                  break. If you take an incomplete break, you will need to submit additional
                  documentation. Payment for worked-through breaks may take up to three business
                  days to process.
                </>
              ) : (
                <>
                  This facility requires a mandatory {mandatoryBreakDurationInMinutes}-minute unpaid
                  break if you work longer than 6 hours. If you take an incomplete break, you will
                  need to submit additional documentation. Payment for worked-through breaks may
                  take up to three business days to process.
                </>
              )}
            </Text>
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
